import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import useBackNavigation from "../../../Veridas/Functionality/useBackNavigation";
import useHandleBeforeUnload from "../../../Veridas/Functionality/useHandleBeforeUnload";
import {getStoredBuildingApartmentId, MODEL_VERIDAS} from "../../../Veridas/Model/Models";
import {BUILDING_DETAIL_MODEL} from "../../Model/Model";
import {buildingRegistrationUrl, toBuildingRegistrationDetail} from "../Model/Model";
import {getUrlParameters} from "../../../Dashboard/Visitor/External/Model/Model";
import routes from "../../../../Routes/routes";
import {GetPublicBuildingApartmentDetails, GetPublicBuildingById} from "../../../../reduxToolkit/Building/BuildingApi";
import {errorMsg, tryGetAddress} from "../../../../Apis/functions";

/**
 *
 * Hook of the last step of the external building registration. It holds the data
 * the identity validation returned and the exit navigation back to the
 * registration invitation.
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @returns {Object} An object containing state variables and handler functions:
 * - modifiedData: Data of the resident read from the validated document.
 * - registrationDetail: Summary of the registration shown in the detail card.
 * - latLng: Coordinates of the building, they gate the location card.
 * - direction: Address resolved from the coordinates.
 * - setLatitude / setLongitude: Callbacks the map needs, the map is read only here.
 * - handleSubmit: Navigates back to the registration invitation.
 */
const BuildingRegistrationSuccessApp = () => {

    const {buildingId, userId} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userDataSuccess = useSelector((state) => state.VisitorSlice.userDataDocument);
    const validationData = useSelector((state) => state.VisitorSlice.getValidationId);
    // Unit and email the resident captured on the identify you step
    const registrationResident = useSelector((state) => state.BuildingSlice.buildingRegistrationResident);
    const [modifiedData, setModifiedData] = useState({...MODEL_VERIDAS});
    const [buildingDetail, setBuildingDetail] = useState({...BUILDING_DETAIL_MODEL});
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [latLng, setLatLng] = useState([]);
    const [direction, setDirection] = useState(null);
    const [apartmentDetail, setApartmentDetail] = useState(null);
    // The registration is finished on this screen, the date of the summary is
    // the moment the resident reached it
    const [registrationDate] = useState(() => new Date().toISOString());

    // The link copied for one resident travels with their apartment in the place
    // of the building, it is the one the detail of this screen is read from
    const buildingApartmentId = registrationResident?.buildingApartmentId ?? getStoredBuildingApartmentId();
    const isApartmentRegistration = !!buildingApartmentId && buildingApartmentId === buildingId;

    // The registration is over, leaving it returns to the invitation the resident
    // opened, the first screen of the flow, and never to the identify you form
    // they already filled. The link copied for one resident carries their user
    // next to the apartment, the one with the building does not
    const mainUrl = getUrlParameters({
        url: buildingRegistrationUrl(
            routes.buildingRegistration.main,
            buildingId,
            isApartmentRegistration ? userId : null
        )
    });

    /**
     * Syncs Redux user validation data into local state.
     *
     * @effect
     * @dependency {userDataSuccess} triggers update when validation data changes
     */
    useEffect(() => {
        if (userDataSuccess) {
            setModifiedData(modifiedData.set(userDataSuccess));
        }
    }, [userDataSuccess]);

    // Loads the building of the registration, the resident has no session here
    useEffect(() => {
        getBuildingDetail();
    }, [buildingId]);

    /**
     * Loads the building the registration belongs to. The flow is external so it
     * goes through the public endpoint instead of the one behind the session.
     * The registration that started from the link of one resident only carries
     * their apartment, the building of the summary is read from it.
     *
     * @function getBuildingDetail
     */
    const getBuildingDetail = () => {
        if (!buildingId) return;

        if (isApartmentRegistration) {
            getApartmentDetail();
            return;
        }

        dispatch(GetPublicBuildingById(buildingId)).then((response) => {
            const {success, data, code} = response?.payload?.data || {};
            if (!success) {
                errorMsg(code);
                return;
            }
            setBuildingDetail(BUILDING_DETAIL_MODEL.set(data));
        });
    };

    /**
     * Loads the apartment of the registration link. It answers the building and
     * the coordinates of the location card, and the unit and the email that
     * close the summary, the resident never captured them on this flow.
     *
     * @function getApartmentDetail
     */
    const getApartmentDetail = () => {
        dispatch(GetPublicBuildingApartmentDetails(buildingApartmentId)).then((response) => {
            const {success, data, code} = response?.payload?.data || {};
            if (!success) {
                errorMsg(code);
                return;
            }
            setApartmentDetail(data);
            setBuildingDetail(toBuildingRegistrationDetail(data));
        });
    };

    /**
     * Summary of the registration. The resident is the one read from the
     * validated document, the building comes from the public endpoint and the
     * unit and the confirmation email are the ones the resident captured, or the
     * ones of the apartment when the registration started from the link of one.
     */
    const registrationDetail = useMemo(() => {
        const documentName = [modifiedData?.name, modifiedData?.lastName, modifiedData?.secondLastName]
            .filter(Boolean)
            .join(' ')
            .trim();

        return {
            buildingName: buildingDetail?.name || '',
            resident: documentName,
            unit: registrationResident?.unit || apartmentDetail?.apartment || '',
            confirmationSentTo: registrationResident?.email || apartmentDetail?.email || '',
            registrationDate,
            zoneName: buildingDetail?.zoneName || ''
        };
    }, [modifiedData, buildingDetail, registrationResident, apartmentDetail, registrationDate]);

    /**
     * Assigns the coordinates of the building and resolves the address shown
     * under the map.
     *
     * @effect
     */
    useEffect(() => {
        const {latitude: lat, longitude: lng} = buildingDetail;

        if (lat && lng) {
            setLatLng([lat, lng]);
            setDirection(t("loading"));
            tryGetAddress(lat, lng, dispatch, setDirection);
        }
    }, [buildingDetail.latitude, buildingDetail.longitude]);

    /**
     * Closes the flow and redirects the resident to the first screen of the
     * registration, the invitation of the building they opened.
     */
    const handleSubmit = () => {
        navigate(mainUrl);
    };

    useBackNavigation({validationId: validationData});
    useHandleBeforeUnload({validationId: validationData});

    return {
        t,
        modifiedData,
        registrationDetail,
        latLng,
        direction,
        setLatitude,
        setLongitude,
        handleSubmit
    }

}

export default BuildingRegistrationSuccessApp
